import React from 'react'
import styled from 'styled-components'
import { AlertCircle, Archive, Download } from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import useAxios from 'axios-hooks'
import _ from 'lodash'

import BreadcrumbHeader from '~/components/BreadcrumbHeader'
import CustomTable from '~/components/CustomTable'
import { ScoreOverview } from '../LocationsList/components/ScoreOverview'
import { checklistMetrics } from '~/constants/checklistMetrics'
import { ReactComponent as CheckIcon } from '../../../assets/img/svg/check-2.svg'
import { ReactComponent as DangerIcon } from '../../../assets/img/svg/danger.svg'
import { Show } from '~/components/commons'
import { StyledSwitch } from '../LocationsList/styles'
import { FormText } from 'reactstrap'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { CLIENT } from '~/constants/roles'
import {
  SearchParamsHandlerProvider,
  useSearchParamsHandler,
} from '~/contexts/FilterContext'
import { NumberParam, StringParam, withDefault } from 'use-query-params'

const IMPACT_TEXT = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
}

const IMPACT_COLORS = {
  0: {
    true: '#f5f5f1',
    false: '#f5f5f1',
    null: '#f5f5f1',
  },
  1: {
    true: '#4FD1C5',
    false: '#319795',
    null: '#319795',
  },
  2: {
    true: '#90CDF4',
    false: '#4299E1',
    null: '#4299E1',
  },
  3: {
    true: '#ABA4F6',
    false: '#7367F0',
    null: '#7367F0',
  },
}

const SORT_BY_TO_KEY = {
  status: 'metric',
  item: 'name',
  impact: 'impact',
  action: 'canEdit',
}

function LocationsScore() {
  const intl = useIntl()
  const currentProjectId = useSelector(state => state.user?.selectedProject?.id)
  const allProjectIds = useSelector(state =>
    state.user?.profile?.projects?.map(item => item.id)
  )
  const { query } = useSearchParamsHandler()
  const { sort, sortBy } = query
  const [allLocations, setAllLocations] = React.useState(
    JSON.parse(localStorage.getItem('state'))?.allLocationsToogle !== undefined
      ? JSON.parse(localStorage.getItem('state'))?.allLocationsToogle
      : true
  )
  const userProfile = useSelector(state => state.user?.profile)
  const userIsClient = userProfile?.role === CLIENT

  const SCORE_COLUMNS = React.useMemo(() => {
    const BASE_COLUMNS = [
      {
        id: 'status',
        title: 'Status',
        width: '15%',
        cell: item => {
          const checked = !+(item?.metric || 0)

          return (
            <ScoreStatus data-checked={checked}>
              {checked ? (
                <>
                  <CheckIcon width="15" />
                  <div>
                    <FormattedMessage id="Done" />
                  </div>
                </>
              ) : (
                <>
                  <DangerIcon width="15" />
                  <div>
                    {item.metric}{' '}
                    {+item.metric === 1 ? (
                      <FormattedMessage id="Pending" />
                    ) : (
                      <FormattedMessage id="Pendings" />
                    )}
                  </div>
                </>
              )}
            </ScoreStatus>
          )
        },
        centered: true,
        sortable: true,
      },
      {
        id: 'item',
        title: 'Item',
        width: '50%',
        sortable: true,
        cell: item => {
          const Icon = item?.icon || Archive
          const checked = !+(item?.metric || 0)

          return (
            <ItemCell done={checked}>
              <div>
                <Icon size="30" />
              </div>
              <div className="item-text">
                <div>
                  <FormattedMessage id={item?.name} />
                </div>
                <div>
                  <FormattedMessage id={item?.description} />
                </div>
              </div>
            </ItemCell>
          )
        },
      },
      {
        id: 'impact',
        title: 'Impact',
        cell: item => {
          const impact = item?.impact
          const impactText = IMPACT_TEXT[impact]
          const impactColor = IMPACT_COLORS[impact][!item?.metric]

          return (
            <ImpactCell>
              <AlertCircle size="23" fill={impactColor} color="#fff" />
              <FormattedMessage id={impactText} />
            </ImpactCell>
          )
        },
        sortable: true,
      },
    ]
    const EDIT_COLUMN = {
      id: 'action',
      width: '10%',
      cell: item => {
        const checked = !+(item?.metric || 0)
        const canEdit = item?.canEdit

        return (
          <>
            {canEdit && !checked && (
              <ScoreUpdate href={item.editUrl}>
                <FormattedMessage id="Update" />
              </ScoreUpdate>
            )}
          </>
        )
      },
      sortable: true,
      title: '',
      centered: true,
    }

    return userIsClient ? BASE_COLUMNS : [...BASE_COLUMNS, EDIT_COLUMN]
  }, [userIsClient])

  const [{ data, loading }] = useAxios({
    url: `/locations/score`,
    method: 'GET',
    params: {
      project: allLocations ? undefined : currentProjectId,
      ...query,
    },
  })

  const [{ loading: csvLoading }, downloadCSV] = useAxios(
    {
      url: `/locations/score/csv`,
      method: 'GET',
      params: {
        project: allLocations ? undefined : currentProjectId,
      },
    },
    {
      useCache: true,
      manual: true,
    }
  )

  function handleToggleAllLocations(bool) {
    setAllLocations(bool)
    const previousState = localStorage.getItem('state')
      ? JSON.parse(localStorage.getItem('state'))
      : {}
    localStorage.setItem(
      'state',
      JSON.stringify({
        ...previousState,
        allLocationsToogle: bool,
      })
    )
  }

  const handleCSV = React.useCallback(() => {
    downloadCSV()
      .then(({ data }) => {
        const blob = new Blob([data], { type: 'data:text/csv;charset=utf-8,' })
        const blobURL = window.URL.createObjectURL(blob)
        // Create new tag for download file
        const anchor = document.createElement('a')
        anchor.download = 'score-report.csv'
        anchor.href = blobURL
        anchor.dataset.downloadurl = [
          'text/csv',
          anchor.download,
          anchor.href,
        ].join(':')
        anchor.click()
        // Remove URL.createObjectURL. The browser should not save the reference to the file.
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          URL.revokeObjectURL(blobURL)
        }, 100)
      })
      .catch(e => {
        toast.error('Error on downloading your CSV file.')
        console.error(e)
      })
  }, [downloadCSV])

  const HeaderMenu = React.useCallback(() => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <CSVButton disabled={csvLoading} onClick={handleCSV}>
          <Download color="#fff" size={15} />
          <FormattedMessage id="Download report" />
        </CSVButton>
        <Show condition={allProjectIds.length > 1}>
          <StyledSwitch
            id="alllocations-switch"
            checked={allLocations}
            style={{ zIndex: '0 !important' }}
            onChange={e => handleToggleAllLocations(e.target.checked)}
            type="switch"
            inline
          >
            <span>
              <FormText className="text-bold-500 d-inline-block">
                <FormattedMessage id="View all account locations" />
              </FormText>
            </span>
          </StyledSwitch>
        </Show>
      </div>
    )
  }, [allLocations, allProjectIds, csvLoading, handleCSV])

  const scoreList = React.useMemo(() => {
    if (!data) return []
    return checklistMetrics
      .map(item => {
        return {
          ...item,
          metric: data[item.id.toLowerCase()],
        }
      })
      .sort((a, b) => {
        const actualSortBy = SORT_BY_TO_KEY[sortBy]
        let firstValue = a[actualSortBy]
        let secondValue = b[actualSortBy]

        if (actualSortBy === 'metric') {
          firstValue = a[actualSortBy] ? +a[actualSortBy] : 0
          secondValue = b[actualSortBy] ? +b[actualSortBy] : 0
        }
        if (actualSortBy === 'name') {
          firstValue = intl.formatMessage({ id: a[actualSortBy] })
          secondValue = intl.formatMessage({ id: b[actualSortBy] })
        }

        if (actualSortBy === 'canEdit') {
          firstValue = a?.metric && a?.canEdit ? 1 : 0
          secondValue = b?.metric && b?.canEdit ? 1 : 0
        }

        if (sort === 'ASC') {
          return firstValue > secondValue ? 1 : -1
        } else if (sort === 'DESC') {
          return firstValue < secondValue ? 1 : -1
        } else {
          return 0
        }
      })
  }, [data, sort, sortBy, intl])

  return (
    <ScoreDiv>
      <BreadcrumbHeader path={[{ name: 'Performance', path: 'performance' }]}>
        <HeaderMenu />
      </BreadcrumbHeader>

      <CustomTable
        hasFilter
        header="Performance"
        columns={SCORE_COLUMNS}
        data={scoreList}
        total={scoreList.length}
        forceLimit={scoreList.length}
        loading={loading}
      >
        <ScoreOverview data={data} loading={false} />
      </CustomTable>
    </ScoreDiv>
  )
}

const ScoreDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  > div {
    white-space: nowrap;
  }
`

const ScoreStatus = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.8rem;
  border-radius: 6px;
  background-color: #fff5f5;
  color: #c53030;

  &[data-checked='true'] {
    background-color: #f0fff4;
    color: #25855a;
  }
`

const CSVButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.8125rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 20px;
  margin: none;
  font-size: 0.875rem;
  background-color: #776cf1;
  color: #fff;
`

const ImpactCell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1em;

  color: #2d3748;
  font-size: 1em;
  font-weight: 600;
`

const ScoreUpdate = styled.a`
  color: #7367f0;
`

const ItemCell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1em;
  padding: 0.3em 0;
  color: ${props => (props?.done ? '#4A5568' : '#718096')};

  .item-text {
    > div:nth-child(1) {
      font-size: 1.2em;
      font-weight: 400;
    }

    > div:nth-child(2) {
      font-size: 1em;
      font-weight: 400;
    }
  }
`

const LocationsScoreWrapper = () => {
  const arrayParam = withDefault(
    {
      encode: array => array,
      decode: array => array?.split(','),
    },
    []
  )
  return (
    <SearchParamsHandlerProvider
      queryParams={{
        states: withDefault(arrayParam, []),
        cities: withDefault(arrayParam, []),
        areas: withDefault(arrayParam, []),
        labels: withDefault(arrayParam, []),
        locations: withDefault(arrayParam, []),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 10),
        sort: withDefault(StringParam, ''),
        sortBy: withDefault(StringParam, ''),
        search: StringParam,
      }}
    >
      <LocationsScore />
    </SearchParamsHandlerProvider>
  )
}

export default LocationsScoreWrapper
